import React, { useEffect, useReducer, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { Row, Col, Typography, Breadcrumb, Card, Space, Spin, Empty, Button, Dropdown, message } from 'antd';
import { Content } from 'antd/es/layout/layout';
import {
	HomeOutlined,
	PlayCircleOutlined,
	PauseCircleOutlined,
	DownloadOutlined,
	SoundOutlined,
	FileImageOutlined,
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../../App.css';
import PropTypes from 'prop-types';

import {
	SPECTROGRAM_LIST,
	SPECTROGRAM_LIST_SUCCESS,
	SPECTROGRAM_LIST_ERROR,
} from './constants';

import englan from '../../english.json';
import germanlan from '../../german.json';
import arabiclan from '../../arabic.json';
import frenchlan from '../../french.json';
import hindilan from '../../hindi.json';
import malayalamlan from '../../malayalam.json';


const { Title } = Typography;

// ============================================================
// Reducer (local to this page)
//   spectrogram : null | { image, frames, mels, duration, sampleRate, fmax }
// ============================================================
const initialState = { spectrogram: null, pending: false, error: false };
// const dashboardtype = process.env.REACT_APP_IDENTIFIER;
// const langid = localStorage.getItem(`${dashboardtype.toUpperCase()}_LANGUAGE_ID`) && localStorage.getItem(`${dashboardtype.toUpperCase()}_LANGUAGE_ID`) ? parseInt(localStorage.getItem(`${dashboardtype.toUpperCase()}_LANGUAGE_ID`)) : 2;
// const langdata = langid && langid ==1 ? arabiclan : langid ==2 ? englan : langid ==3 ? frenchlan : langid ==4 ? germanlan : langid ==5 ? hindilan : langid == 6 ? malayalamlan : englan;

const spectrogramReducer = (state, action) => {
	switch (action.type) {
	case SPECTROGRAM_LIST:
		return { ...state, pending: true, error: false, spectrogram: null };
	case SPECTROGRAM_LIST_SUCCESS:
		return { ...state, pending: false, error: false, spectrogram: action.payload };
	case SPECTROGRAM_LIST_ERROR:
		return { ...state, pending: false, error: true, spectrogram: null };
	default:
		return state;
	}
};

// ============================================================
// DSP helpers
// ============================================================

// In-place iterative radix-2 FFT (length must be a power of two).
const fft = (re, im) => {
	const n = re.length;

	for (let i = 1, j = 0; i < n; i += 1) {
		let bit = n >> 1;
		for (; j & bit; bit >>= 1) j ^= bit;
		j ^= bit;
		if (i < j) {
			[re[i], re[j]] = [re[j], re[i]];
			[im[i], im[j]] = [im[j], im[i]];
		}
	}

	for (let len = 2; len <= n; len <<= 1) {
		const ang = (-2 * Math.PI) / len;
		const wRe = Math.cos(ang);
		const wIm = Math.sin(ang);
		const half = len / 2;
		for (let i = 0; i < n; i += len) {
			let curRe = 1;
			let curIm = 0;
			for (let k = 0; k < half; k += 1) {
				const a = i + k;
				const b = a + half;
				const vRe = re[b] * curRe - im[b] * curIm;
				const vIm = re[b] * curIm + im[b] * curRe;
				re[b] = re[a] - vRe;
				im[b] = im[a] - vIm;
				re[a] += vRe;
				im[a] += vIm;
				const nextRe = curRe * wRe - curIm * wIm;
				curIm = curRe * wIm + curIm * wRe;
				curRe = nextRe;
			}
		}
	}
};

const hzToMel = (hz) => 2595 * Math.log10(1 + hz / 700);
const melToHz = (mel) => 700 * (Math.pow(10, mel / 2595) - 1);

// Triangular mel filterbank, stored sparsely: [{ start, weights }]. Each
// filter is normalised to unit sum so bands of different widths are comparable.
const buildMelFilterbank = (sampleRate, fftSize, nMels, fmax) => {
	const binHz = sampleRate / fftSize;
	const maxMel = hzToMel(fmax);
	const points = [];
	for (let i = 0; i < nMels + 2; i += 1) {
		points.push(melToHz((maxMel * i) / (nMels + 1)));
	}

	const filters = [];
	for (let m = 0; m < nMels; m += 1) {
		const fl = points[m];
		const fc = points[m + 1];
		const fh = points[m + 2];

		const kStart = Math.max(0, Math.ceil(fl / binHz));
		const kEnd = Math.min(fftSize / 2 - 1, Math.floor(fh / binHz));
		let weights = [];
		let start = kStart;
		let sum = 0;

		for (let k = kStart; k <= kEnd; k += 1) {
			const f = k * binHz;
			const w = f <= fc ? (f - fl) / (fc - fl) : (fh - f) / (fh - fc);
			const ww = Math.max(0, w);
			weights.push(ww);
			sum += ww;
		}

		// Very narrow low-frequency filters can miss every FFT bin ->
		// interpolate between the two nearest bins instead (no black stripes).
		if (sum <= 0) {
			const kf = fc / binHz;
			const k0 = Math.min(fftSize / 2 - 2, Math.floor(kf));
			const frac = kf - k0;
			start = k0;
			weights = [1 - frac, frac];
			sum = 1;
		}

		filters.push({ start, weights: weights.map((w) => w / sum) });
	}
	return filters;
};

// Mel spectrogram in dB (relative to the loudest cell, floored at -topDb),
// returned as a Uint8 image [frames * mels] with 0..255 intensity.
const computeMelSpectrogram = (samples, sampleRate, options = {}) => {
	const { fftSize = 2048, nMels = 128, maxFrames = 1600, topDb = 80 } = options;
	const fmax = sampleRate / 2;

	const hann = new Float32Array(fftSize);
	for (let i = 0; i < fftSize; i += 1) {
		hann[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (fftSize - 1));
	}

	const total = samples.length;
	const hop = Math.max(512, Math.ceil(Math.max(total - fftSize, 1) / maxFrames));
	const frames = total <= fftSize ? 1 : Math.floor((total - fftSize) / hop) + 1;
	const filters = buildMelFilterbank(sampleRate, fftSize, nMels, fmax);

	const db = new Float32Array(frames * nMels);
	const re = new Float64Array(fftSize);
	const im = new Float64Array(fftSize);
	const power = new Float64Array(fftSize / 2);
	let maxDb = -Infinity;

	for (let f = 0; f < frames; f += 1) {
		const offset = f * hop;
		for (let i = 0; i < fftSize; i += 1) {
			const s = offset + i < total ? samples[offset + i] : 0;
			re[i] = s * hann[i];
			im[i] = 0;
		}
		fft(re, im);

		for (let k = 0; k < fftSize / 2; k += 1) {
			power[k] = re[k] * re[k] + im[k] * im[k];
		}

		for (let m = 0; m < nMels; m += 1) {
			const { start, weights } = filters[m];
			let acc = 0;
			for (let j = 0; j < weights.length; j += 1) {
				acc += weights[j] * power[start + j];
			}
			const value = 10 * Math.log10(Math.max(acc, 1e-10));
			db[f * nMels + m] = value;
			if (value > maxDb) maxDb = value;
		}
	}

	const image = new Uint8Array(frames * nMels);
	for (let i = 0; i < image.length; i += 1) {
		const rel = Math.max(-topDb, db[i] - maxDb); // -topDb .. 0
		image[i] = Math.round(((rel + topDb) / topDb) * 255);
	}

	return {
		image,
		frames,
		mels: nMels,
		fmax,
		sampleRate,
		duration: total / sampleRate,
		topDb,
	};
};

// Decode the raw audio bytes (wav / mp3 / ogg ...) into mono samples.
const decodeAudio = async (arrayBuffer) => {
	const AudioCtx = window.AudioContext || window.webkitAudioContext;
	const ctx = new AudioCtx();
	try {
		const audioBuffer = await ctx.decodeAudioData(arrayBuffer.slice(0));
		return { samples: audioBuffer.getChannelData(0), sampleRate: audioBuffer.sampleRate };
	} finally {
		if (ctx.close) ctx.close();
	}
};

// ============================================================
// "magma" colour map (same look as the librosa / matplotlib example)
// ============================================================
const MAGMA_STOPS = ['#000004', '#180f3e', '#451077', '#721f81', '#9f2f7f', '#cd4071', '#f1605d', '#fd9567', '#feca8d', '#fcfdbf'];

const buildMagmaLut = () => {
	const rgb = MAGMA_STOPS.map((hex) => [
		parseInt(hex.slice(1, 3), 16),
		parseInt(hex.slice(3, 5), 16),
		parseInt(hex.slice(5, 7), 16),
	]);
	const lut = new Uint8Array(256 * 3);
	for (let i = 0; i < 256; i += 1) {
		const pos = (i / 255) * (rgb.length - 1);
		const a = Math.min(rgb.length - 2, Math.floor(pos));
		const t = pos - a;
		for (let c = 0; c < 3; c += 1) {
			lut[i * 3 + c] = Math.round(rgb[a][c] + (rgb[a + 1][c] - rgb[a][c]) * t);
		}
	}
	return lut;
};
const MAGMA_LUT = buildMagmaLut();

const Y_TICK_CANDIDATES = [0, 100, 250, 500, 1000, 2000, 4000, 8000, 16000];
const X_STEP_CANDIDATES = [0.1, 0.25, 0.5, 1, 2, 5, 10, 15, 30, 60, 120, 300];

const formatClock = (seconds) => {
	const s = Math.max(0, seconds || 0);
	const m = Math.floor(s / 60);
	return `${m}:${(s - m * 60).toFixed(1).padStart(4, '0')}`;
};

// Pick a file extension from the audio content-type.
const getAudioExt = (contentType) => {
	const type = (contentType || '').toLowerCase();
	if (type.includes('mpeg') || type.includes('mp3')) return 'mp3';
	if (type.includes('ogg')) return 'ogg';
	if (type.includes('webm')) return 'webm';
	if (type.includes('aac')) return 'aac';
	if (type.includes('flac')) return 'flac';
	return 'wav';
};

const triggerDownload = (href, filename) => {
	const a = document.createElement('a');
	a.href = href;
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
};

// ============================================================
// Spectrogram image + play controls
//   controlsHost : DOM node (next to the breadcrumb) where the
//                  play/pause icon + time are rendered via a portal.
// ============================================================
const SpectrogramPlayer = ({ spectrogram, audioUrl, audioExt, fileName, controlsHost, langdata }) => {
	const { image, frames, mels, duration, fmax, topDb } = spectrogram;

	const canvasRef = useRef(null);
	const plotRef = useRef(null);
	const audioRef = useRef(null);
	const playheadRef = useRef(null);
	const timeRef = useRef(null);
	const rafRef = useRef(null);
	const [playing, setPlaying] = useState(false);

	// Paint the spectrogram image once (low-res canvas, stretched by CSS).
	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;
		canvas.width = frames;
		canvas.height = mels;
		const ctx = canvas.getContext('2d');
		const img = ctx.createImageData(frames, mels);
		for (let f = 0; f < frames; f += 1) {
			for (let m = 0; m < mels; m += 1) {
				const v = image[f * mels + m];
				const y = mels - 1 - m; // highest frequency at the top
				const px = (y * frames + f) * 4;
				img.data[px] = MAGMA_LUT[v * 3];
				img.data[px + 1] = MAGMA_LUT[v * 3 + 1];
				img.data[px + 2] = MAGMA_LUT[v * 3 + 2];
				img.data[px + 3] = 255;
			}
		}
		ctx.putImageData(img, 0, 0);
	}, [spectrogram]);

	const updatePlayhead = () => {
		const audio = audioRef.current;
		if (!audio) return;
		const t = audio.currentTime;
		const pct = Math.min(100, Math.max(0, (t / duration) * 100));
		// The playhead line is hidden until playback starts.
		const started = t > 0 || !audio.paused;
		if (playheadRef.current) {
			playheadRef.current.style.left = `${pct}%`;
			playheadRef.current.style.display = started ? 'block' : 'none';
		}
		if (timeRef.current) timeRef.current.textContent = `${formatClock(t)} / ${formatClock(duration)}`;
	};

	const stopLoop = () => {
		if (rafRef.current) {
			cancelAnimationFrame(rafRef.current);
			rafRef.current = null;
		}
	};

	const startLoop = () => {
		stopLoop();
		const tick = () => {
			updatePlayhead();
			rafRef.current = requestAnimationFrame(tick);
		};
		rafRef.current = requestAnimationFrame(tick);
	};

	useEffect(() => () => {
		stopLoop();
		if (audioRef.current) audioRef.current.pause();
	}, []);

	const togglePlay = () => {
		const audio = audioRef.current;
		if (!audio) return;
		if (audio.paused) {
			audio.play().catch((error) => {
				console.log(error);
				message.error(langdata && langdata.SomethingWentWrong ? langdata.SomethingWentWrong : 'Something Went Wrong!');
			});
		} else {
			audio.pause();
		}
	};

	// Click anywhere on the spectrogram to jump there.
	const handleSeek = (event) => {
		const audio = audioRef.current;
		const plot = plotRef.current;
		if (!audio || !plot) return;
		const rect = plot.getBoundingClientRect();
		const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
		audio.currentTime = ratio * duration;
		updatePlayhead();
	};

	// ---- axis ticks ----
	const maxMel = hzToMel(fmax);
	const yTicks = Y_TICK_CANDIDATES.filter((hz) => hz < fmax * 0.97).map((hz) => ({
		hz,
		pct: (hzToMel(hz) / maxMel) * 100,
	}));

	const xStep = X_STEP_CANDIDATES.find((step) => duration / step <= 12) || 600;
	const xTicks = [];
	for (let t = 0; t <= duration + 1e-9; t += xStep) {
		xTicks.push({ t, pct: (t / duration) * 100 });
	}
	const xDecimals = xStep < 1 ? (xStep < 0.25 ? 2 : 1) : (xStep % 1 ? 1 : 0);

	const PLOT_HEIGHT = 420;

	// ---- downloads ----
	const downloadAudio = () => {
		if (!audioUrl) return;
		triggerDownload(audioUrl, `${fileName}.${audioExt || 'wav'}`);
	};

	// Renders a full figure (spectrogram + axes + colour bar) to a PNG.
	const downloadPng = () => {
		const src = canvasRef.current;
		if (!src) return;

		const W = 1600;
		const H = 700;
		const L = 100;
		const R = 140;
		const T = 30;
		const B = 80;
		const pw = W - L - R;
		const ph = H - T - B;

		const out = document.createElement('canvas');
		out.width = W;
		out.height = H;
		const ctx = out.getContext('2d');

		ctx.fillStyle = '#ffffff';
		ctx.fillRect(0, 0, W, H);

		// spectrogram image
		ctx.imageSmoothingEnabled = true;
		ctx.drawImage(src, L, T, pw, ph);

		ctx.font = '18px sans-serif';
		ctx.fillStyle = '#000000';
		ctx.strokeStyle = '#000000';
		ctx.lineWidth = 1;

		// y grid + labels
		ctx.textAlign = 'right';
		ctx.textBaseline = 'middle';
		yTicks.forEach((tick) => {
			const y = T + ph * (1 - tick.pct / 100);
			ctx.save();
			ctx.strokeStyle = 'rgba(255,255,255,0.15)';
			ctx.beginPath();
			ctx.moveTo(L, y);
			ctx.lineTo(L + pw, y);
			ctx.stroke();
			ctx.restore();
			ctx.beginPath();
			ctx.moveTo(L - 6, y);
			ctx.lineTo(L, y);
			ctx.stroke();
			ctx.fillText(tick.hz.toLocaleString(), L - 10, y);
		});

		// x labels
		ctx.textAlign = 'center';
		ctx.textBaseline = 'top';
		xTicks.forEach((tick) => {
			const x = L + (pw * tick.pct) / 100;
			ctx.beginPath();
			ctx.moveTo(x, T + ph);
			ctx.lineTo(x, T + ph + 6);
			ctx.stroke();
			ctx.fillText(tick.t.toFixed(xDecimals), x, T + ph + 10);
		});

		// plot border
		ctx.strokeRect(L, T, pw, ph);

		// axis titles
		ctx.font = '20px sans-serif';
		ctx.textAlign = 'center';
		ctx.textBaseline = 'bottom';
		ctx.fillText(`${langdata && langdata.Time ? langdata.Time : 'Time'} (s)`, L + pw / 2, H - 8);
		ctx.save();
		ctx.translate(26, T + ph / 2);
		ctx.rotate(-Math.PI / 2);
		ctx.textBaseline = 'middle';
		ctx.fillText(`${langdata && langdata.Frequency ? langdata.Frequency : 'Frequency'} (Hz)`, 0, 0);
		ctx.restore();

		// colour bar
		const cbX = L + pw + 30;
		const grad = ctx.createLinearGradient(0, T + ph, 0, T);
		MAGMA_STOPS.forEach((color, i) => grad.addColorStop(i / (MAGMA_STOPS.length - 1), color));
		ctx.fillStyle = grad;
		ctx.fillRect(cbX, T, 24, ph);
		ctx.strokeRect(cbX, T, 24, ph);
		ctx.fillStyle = '#000000';
		ctx.font = '16px sans-serif';
		ctx.textAlign = 'left';
		ctx.textBaseline = 'top';
		ctx.fillText('0 dB', cbX + 32, T);
		ctx.textBaseline = 'middle';
		ctx.fillText(`-${topDb / 2} dB`, cbX + 32, T + ph / 2);
		ctx.textBaseline = 'bottom';
		ctx.fillText(`-${topDb} dB`, cbX + 32, T + ph);

		out.toBlob((blob) => {
			if (!blob) return;
			const url = URL.createObjectURL(blob);
			triggerDownload(url, `${fileName}.png`);
			setTimeout(() => URL.revokeObjectURL(url), 1000);
		}, 'image/png');
	};

	const downloadItems = [
		{
			key: 'audio',
			icon: <SoundOutlined />,
			label: langdata && langdata.DownloadAudioFile ? langdata.DownloadAudioFile : 'Download audio file',
			disabled: !audioUrl,
		},
		{
			key: 'png',
			icon: <FileImageOutlined />,
			label: langdata && langdata.DownloadPNG ? langdata.DownloadPNG : 'Download PNG',
		},
	];

	const handleDownloadClick = ({ key }) => {
		if (key === 'audio') downloadAudio();
		else if (key === 'png') downloadPng();
	};

	// Play / pause icon + time, rendered at the top right (next to the breadcrumb).
	const controls = (
		<div style={{ display: 'flex', alignItems: 'center' }}>
			<Button
				type="text"
				shape="circle"
				aria-label={playing ? 'Pause' : 'Play'}
				icon={playing
					? <PauseCircleOutlined style={{ fontSize: '30px', color: 'rgb(65, 60, 60)' }} />
					: <PlayCircleOutlined style={{ fontSize: '30px', color: 'rgb(65, 60, 60)' }} />}
				onClick={togglePlay}
				disabled={!audioUrl}
				style={{ width: '40px', height: '40px' }}
			/>
			<span ref={timeRef} style={{ marginLeft: '8px', fontVariantNumeric: 'tabular-nums' }}>
				{`${formatClock(0)} / ${formatClock(duration)}`}
			</span>
		</div>
	);

	return (
		<div>
			{controlsHost ? createPortal(controls, controlsHost) : null}

			{/* hidden audio element that drives the playhead */}
			<audio
				ref={audioRef}
				src={audioUrl || undefined}
				preload="auto"
				onPlay={() => { setPlaying(true); startLoop(); }}
				onPause={() => { setPlaying(false); stopLoop(); updatePlayhead(); }}
				onEnded={() => { setPlaying(false); stopLoop(); updatePlayhead(); }}
				onSeeked={updatePlayhead}
			/>

			<div style={{ display: 'flex', alignItems: 'stretch' }}>
				{/* rotated y-axis title */}
				<div style={{ width: '22px', position: 'relative', height: `${PLOT_HEIGHT}px` }}>
					<span style={{
						position: 'absolute',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%) rotate(-90deg)',
						whiteSpace: 'nowrap',
						fontSize: '12px',
					}}
					>
						{langdata && langdata.Frequency ? langdata.Frequency : 'Frequency'} (Hz)
					</span>
				</div>

				{/* y tick labels */}
				<div style={{ width: '44px', position: 'relative', height: `${PLOT_HEIGHT}px` }}>
					{yTicks.map((tick) => (
						<span
							key={`y-${tick.hz}`}
							style={{
								position: 'absolute',
								right: '6px',
								bottom: `${tick.pct}%`,
								transform: 'translateY(50%)',
								fontSize: '11px',
								lineHeight: 1,
							}}
						>
							{tick.hz.toLocaleString()}
						</span>
					))}
				</div>

				{/* plot area */}
				<div style={{ flex: 1, minWidth: 0 }}>
					<div
						ref={plotRef}
						onClick={handleSeek}
						style={{
							position: 'relative',
							height: `${PLOT_HEIGHT}px`,
							background: '#000004',
							cursor: 'pointer',
							overflow: 'hidden',
						}}
					>
						<canvas
							ref={canvasRef}
							style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
						/>
						{yTicks.map((tick) => (
							<div
								key={`grid-${tick.hz}`}
								style={{
									position: 'absolute',
									left: 0,
									right: 0,
									bottom: `${tick.pct}%`,
									borderTop: '1px solid rgba(255,255,255,0.12)',
									pointerEvents: 'none',
								}}
							/>
						))}
						{/* playhead */}
						<div
							ref={playheadRef}
							style={{
								position: 'absolute',
								top: 0,
								bottom: 0,
								left: '0%',
								width: '2px',
								marginLeft: '-1px',
								background: '#00e5ff',
								boxShadow: '0 0 6px #00e5ff',
								pointerEvents: 'none',
								display: 'none',
							}}
						/>
					</div>

					{/* x tick labels */}
					<div style={{ position: 'relative', height: '22px' }}>
						{xTicks.map((tick) => (
							<span
								key={`x-${tick.t}`}
								style={{
									position: 'absolute',
									left: `${tick.pct}%`,
									top: '4px',
									transform: 'translateX(-50%)',
									fontSize: '11px',
								}}
							>
								{tick.t.toFixed(xDecimals)}
							</span>
						))}
					</div>
					<div style={{ textAlign: 'center', fontSize: '12px', marginTop: '2px' }}>
						{langdata && langdata.Time ? langdata.Time : 'Time'} (s)
					</div>
				</div>

				{/* colour bar */}
				<div style={{ width: '70px', marginLeft: '12px', position: 'relative', height: `${PLOT_HEIGHT}px` }}>
					<div style={{
						position: 'absolute',
						top: 0,
						bottom: 0,
						left: 0,
						width: '14px',
						background: `linear-gradient(to top, ${MAGMA_STOPS.join(', ')})`,
					}}
					/>
					<span style={{ position: 'absolute', left: '20px', top: 0, fontSize: '11px', lineHeight: 1 }}>0 dB</span>
					<span style={{ position: 'absolute', left: '20px', top: '50%', transform: 'translateY(-50%)', fontSize: '11px', lineHeight: 1 }}>-{topDb / 2} dB</span>
					<span style={{ position: 'absolute', left: '20px', bottom: 0, fontSize: '11px', lineHeight: 1 }}>-{topDb} dB</span>
				</div>
			</div>

			{/* download button - bottom left */}
			<div style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-start' }}>
				<Dropdown
					menu={{ items: downloadItems, onClick: handleDownloadClick }}
					trigger={['click']}
					placement="topLeft"
				>
					<Button style={{backgroundColor:'#1677ff',color:'white',border:'none'}} icon={<DownloadOutlined  />}>
						{langdata && langdata.Download ? langdata.Download : 'Download'}
					</Button>
				</Dropdown>
			</div>
		</div>
	);
};

SpectrogramPlayer.propTypes = {
	spectrogram: PropTypes.shape({
		image: PropTypes.instanceOf(Uint8Array).isRequired,
		frames: PropTypes.number.isRequired,
		mels: PropTypes.number.isRequired,
		duration: PropTypes.number.isRequired,
		fmax: PropTypes.number.isRequired,
		topDb: PropTypes.number.isRequired,
		sampleRate: PropTypes.number,
	}).isRequired,
	audioUrl: PropTypes.string,
	audioExt: PropTypes.string,
	fileName: PropTypes.string,
	controlsHost: PropTypes.object,
	langdata: PropTypes.object,
};

export default function Spectrogram() {

	const dashboardtype = process.env.REACT_APP_IDENTIFIER;
	const authtoken = localStorage.getItem(`${dashboardtype.toUpperCase()}_ACCESS_TOKEN`);
	const { state } = useLocation();
	const navigate = useNavigate();

	const apiUrl = localStorage.getItem(`${dashboardtype.toUpperCase()}_API_URL`);
	const api = apiUrl ? apiUrl : process.env.REACT_APP_CORE_HOST;

	const envTimeout = process.env.REACT_APP_TIMEOUT == undefined || process.env.REACT_APP_TIMEOUT == 'undefined' ? 5000 : process.env.REACT_APP_TIMEOUT;
	const savedTimeout = localStorage.getItem(`${dashboardtype.toUpperCase()}_TIMEOUT`);
	const timeout = savedTimeout == null || savedTimeout == 'null' || savedTimeout == 'undefined' ? parseInt(envTimeout) : parseInt(savedTimeout);

	const langid = localStorage.getItem(`${dashboardtype.toUpperCase()}_LANGUAGE_ID`) ? parseInt(localStorage.getItem(`${dashboardtype.toUpperCase()}_LANGUAGE_ID`)) : 2;
	const langdata = langid == 1 ? arabiclan : langid == 2 ? englan : langid == 3 ? frenchlan : langid == 4 ? germanlan : langid == 5 ? hindilan : langid == 6 ? malayalamlan : englan;

	const [spectrogramState, dispatch] = useReducer(spectrogramReducer, initialState);
	const { spectrogram, pending } = spectrogramState;

	// Object URL of the audio response, used by the <audio> element.
	const [audioUrl, setAudioUrl] = useState(null);
	const [audioExt, setAudioExt] = useState('wav');

	// DOM node (top right of the breadcrumb row) that hosts the play controls.
	const [controlsHost, setControlsHost] = useState(null);

	const record = state && state.data ? state.data : null;

	useEffect(() => {
		if (!state) {
			navigate(-1);
		}
	}, [state]);

	// Release the blob URL when it is replaced / the page unmounts.
	useEffect(() => () => {
		if (audioUrl) URL.revokeObjectURL(audioUrl);
	}, [audioUrl]);

	// Fetch + decode whenever we have a record id.
	useEffect(() => {
		if (!record || !record.id) return undefined;

		const controller = new AbortController();
		let cancelled = false;

		const loadSpectrogram = () => {
			dispatch({ type: SPECTROGRAM_LIST });
			setAudioUrl(null);

			const config = {
				method: 'get',
				maxBodyLength: Infinity,
				url: `${api}/spectrogram/${record.id}`,
				timeout: timeout,
				responseType: 'arraybuffer', // the API returns an audio file
				signal: controller.signal,
				headers: {
					Authorization: `Bearer ${authtoken}`,
				},
			};

			axios
				.request(config)
				.then(async (response) => {
					if (cancelled) return;
					if (response.status !== 200 || !response.data || response.data.byteLength === 0) {
						dispatch({ type: SPECTROGRAM_LIST_ERROR });
						return;
					}
					try {
						const { samples, sampleRate } = await decodeAudio(response.data);
						if (cancelled) return;
						if (!samples || samples.length === 0) {
							dispatch({ type: SPECTROGRAM_LIST_ERROR });
							return;
						}

						const contentType = response.headers && response.headers['content-type'];
						const blobType = contentType && contentType.startsWith('audio') ? contentType : 'audio/wav';
						const blob = new Blob([response.data], { type: blobType });
						setAudioExt(getAudioExt(blobType));
						setAudioUrl(URL.createObjectURL(blob));

						dispatch({
							type: SPECTROGRAM_LIST_SUCCESS,
							payload: computeMelSpectrogram(samples, sampleRate),
						});
					} catch (decodeError) {
						console.log(decodeError);
						if (!cancelled) dispatch({ type: SPECTROGRAM_LIST_ERROR });
					}
				})
				.catch((error) => {
					if (cancelled || axios.isCancel(error)) return;
					dispatch({ type: SPECTROGRAM_LIST_ERROR });
					message.config({
						duration: 5,
						maxCount: 1,
					});
					if (error.code == 'ECONNABORTED' || error.code == 'ERR_NETWORK') {
						message.open({
							type: 'error',
							content: langdata && langdata.ItseemsthenetworkisunreachablePleaserefreshtheapplication ? langdata.ItseemsthenetworkisunreachablePleaserefreshtheapplication : 'It seems the network is unreachable. Please refresh the application.',
						});
					} else {
						message.open({
							type: 'error',
							content: `${langdata && langdata.SomethingWentWrong ? langdata.SomethingWentWrong : 'Something Went Wrong!'}`,
						});
					}
				});
		};

		loadSpectrogram();

		return () => {
			cancelled = true;
			controller.abort();
		};
	}, [record && record.id, authtoken]);

	const hasData = !!spectrogram && spectrogram.frames > 0;

	const downloadFileName = `spectrogram_${record && record.channelName ? String(record.channelName).replace(/[^\w-]+/g, '_') : 'audio'}_${record && record.id ? record.id : ''}`;

	return (
		<>
			{state ?
				<>
					<Row
						gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}
						style={{
							margin: '20px',
							paddingLeft: '10px',
							paddingRight: '10px',
							background: '#FFFFFF',
							alignItems: 'center',
							marginTop: '10px',
							marginBottom: '0px'
						}}
					>
						<Col style={{ marginTop: '10px' }} span={24}>
							<Row>
								<Space>
									<Title level={5} style={{ lineHeight: '2', fontWeight: '1' }}> {langdata && langdata.Channel ? langdata.Channel : 'Channel'}: <b>{record ? record.channelName : ''}</b> </Title>
								</Space>
								<Space>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</Space>
								<Space>
									<Title level={5} style={{ lineHeight: '2', fontWeight: '1' }}> {langdata && langdata.Date ? langdata.Date : 'Date'}: <b> {record && record.starttime ? record.starttime : '-'}</b> </Title>
								</Space>
								{hasData ? (
									<>
										<Space>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</Space>
										<Space>
											<Title level={5} style={{ lineHeight: '2', fontWeight: '1' }}> {langdata && langdata.Duration ? langdata.Duration : 'Duration'}: <b>{spectrogram.duration.toFixed(2)} s</b> </Title>
										</Space>
										<Space>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</Space>
										<Space>
											<Title level={5} style={{ lineHeight: '2', fontWeight: '1' }}> {langdata && langdata.SampleRate ? langdata.SampleRate : 'Sample Rate'}: <b>{spectrogram.sampleRate} Hz</b> </Title>
										</Space>
									</>
								) : null}
							</Row>
						</Col>
					</Row>

					<Row
						style={{
							margin: '10px',
							paddingLeft: '10px',
							paddingRight: '10px',
							alignItems: 'center',
							marginTop: '10px',
							marginBottom: '0px'
						}}
					>
						<Col className="gutter-row" span={24}>
							<Card>
								{/* breadcrumb (left) + play controls (right) */}
								<div
									style={{
										display: 'flex',
										justifyContent: 'space-between',
										alignItems: 'center',
										marginBottom: '10px',
									}}
								>
									<Breadcrumb
										items={[
											{
												href: '/dashboard',
												title: <HomeOutlined />,
											},
											{
												title: (
													<a onClick={() => { navigate(-1); }}>
														{state.path === 'realtimedetections'
															? (langdata && langdata.RealtimeDetection ? langdata.RealtimeDetection : 'Realtime Detection')
															: (langdata && langdata.LineWalkerRealtimeDetections ? langdata.LineWalkerRealtimeDetections : 'Line Walker Realtime Detections')}
													</a>
												),
											},
											{
												title: `${langdata && langdata.Spectrogram ? langdata.Spectrogram : 'Spectrogram'}`,
											},
										]}
									/>
									<div ref={setControlsHost} style={{ display: 'flex', alignItems: 'center' }} />
								</div>

								<Content>
									<Spin spinning={pending}>
										<div style={{ minHeight: '400px' }}>
											{hasData ? (
												<SpectrogramPlayer
													key={audioUrl || 'no-audio'}
													spectrogram={spectrogram}
													audioUrl={audioUrl}
													audioExt={audioExt}
													fileName={downloadFileName}
													controlsHost={controlsHost}
													langdata={langdata}
												/>
											) : (
												!pending && (
													<Empty
														image={Empty.PRESENTED_IMAGE_SIMPLE}
														description={langdata && langdata.NoData ? langdata.NoData : 'No Data'}
														style={{ paddingTop: '120px' }}
													/>
												)
											)}
										</div>
									</Spin>
								</Content>
							</Card>
						</Col>
					</Row>
				</>
				: ''}
		</>
	);
}